import React, { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Download, RefreshCw, AlertTriangle, ShoppingBasket, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { api } from "../../lib/api";

/**
 * Phase 34 — Admin page to trigger and monitor Mercadona catalog imports.
 *
 * Actions:
 *  · "Importar catálogo completo" (destructive): wipes every product +
 *    category and re-imports everything from Mercadona (~14 min).
 *  · "Re-sync precios": non-destructive; walks the existing Mercadona
 *    products and refreshes prices + availability only.
 *
 * The endpoint returns a `job_id` immediately; we poll `/import/status?job_id=…`
 * every 2 s so the admin sees progress in real time.
 */
export default function ImportarMercadonaPage() {
  const [job, setJob] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [aliases, setAliases] = useState(null);
  const pollTimer = useRef(null);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/mercadona/import/history?limit=10");
      const items = data.items || [];
      setHistory(items);
      // Phase 35 — auto-load the latest job that carries exclusion counters
      // (or the most-recent completed one) so admins can review stats without
      // triggering a new import. Only hydrates when `job` is still null so
      // we never clobber a live-polling job.
      setJob((prev) => {
        if (prev) return prev;
        const preferred =
          items.find((j) => (j.stats?.excluded_top_categories || 0) > 0) ||
          items.find((j) => j.status === "completed") ||
          items[0];
        return preferred || null;
      });
    } catch (e) {
      // silent — history isn't critical.
    }
  }, []);

  const loadAliases = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/curation/known-aliases");
      setAliases(data.buckets || {});
    } catch (e) {
      // silent — banner is informational.
    }
  }, []);

  const pollJob = useCallback(async (jobId) => {
    try {
      const { data } = await api.get(`/admin/mercadona/import/status?job_id=${jobId}`);
      setJob(data);
      if (data.status === "running") {
        pollTimer.current = setTimeout(() => pollJob(jobId), 2000);
      } else {
        loadHistory();
        toast.success(
          data.status === "completed"
            ? "Importación finalizada"
            : `Importación ${data.status}`
        );
      }
    } catch (e) {
      toast.error("Error consultando el estado del job");
    }
  }, [loadHistory]);

  const startFullImport = async () => {
    setConfirmOpen(false);
    setLoading(true);
    try {
      const { data } = await api.post("/admin/mercadona/import/full", {
        confirm_wipe: true,
      });
      toast.info(`Job iniciado · ${data.job_id.slice(0, 8)}`);
      pollJob(data.job_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo iniciar la importación");
    } finally {
      setLoading(false);
    }
  };

  const startResync = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/mercadona/import/resync-prices", {});
      toast.info(`Job iniciado · ${data.job_id.slice(0, 8)}`);
      pollJob(data.job_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo iniciar el re-sync");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
    loadAliases();
    return () => {
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
  }, [loadHistory, loadAliases]);

  const isRunning = job?.status === "running";
  const progressPct = job?.progress?.total
    ? Math.min(100, Math.round((job.progress.current / job.progress.total) * 100))
    : 0;

  return (
    <div data-testid="importar-mercadona-page" className="space-y-6 max-w-4xl">
      <header>
        <h1 className="text-2xl font-black tracking-tight flex items-center gap-2">
          <ShoppingBasket className="h-6 w-6 text-[#00953B]" /> Importar catálogo Mercadona
        </h1>
        <p className="text-sm text-neutral-500 mt-1">
          Sincroniza el catálogo público de Mercadona con MercaGo. La importación completa
          borra los productos y categorías actuales; el re-sync sólo actualiza precios.
        </p>
      </header>

      <section className="rounded-xl border border-neutral-200 bg-white p-4 space-y-4">
        <h2 className="text-sm font-semibold text-neutral-900">Acciones</h2>
        <div className="flex flex-wrap gap-3">
          <Button
            data-testid="import-full-btn"
            disabled={isRunning || loading}
            onClick={() => setConfirmOpen(true)}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            <Download className="h-4 w-4 mr-2" />
            Importar catálogo completo (~14 min)
          </Button>
          <Button
            data-testid="import-resync-btn"
            disabled={isRunning || loading}
            onClick={startResync}
            variant="outline"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Re-sync sólo precios
          </Button>
        </div>
        <p className="text-xs text-neutral-500">
          Los productos con <code className="bg-neutral-100 px-1 rounded">manual_override=true</code>{" "}
          no se tocan durante un re-sync. Todos los productos importados llevan{" "}
          <code className="bg-neutral-100 px-1 rounded">image_ai_protected=true</code>{" "}
          para bloquear regeneración por IA.
        </p>
      </section>

      {/* Phase 35 — informational banner listing Muslim/Sahrawi-audience
          exclusions applied automatically during import. Fetched from
          `/admin/curation/known-aliases` so the buckets never drift
          from the source of truth in `services/mercadona_exclusions.py`. */}
      {aliases && Object.keys(aliases).length > 0 && (
        <section
          data-testid="import-exclusions-banner"
          className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-2"
        >
          <div className="flex items-start gap-2">
            <ShieldOff className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-amber-900">
                Categorías excluidas automáticamente (Phase 35 curación)
              </h3>
              <p className="text-xs text-amber-800 mt-1">
                Las siguientes categorías <b>NO</b> se importarán aunque Mercadona las publique. Se
                omiten en el pipeline para adaptarse al público objetivo (familias musulmanas de los
                campamentos saharauis).
              </p>
              <ul
                data-testid="import-exclusions-list"
                className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-amber-900"
              >
                {Object.entries(aliases).map(([bucket, names]) => (
                  <li key={bucket}>
                    <span className="font-semibold capitalize">
                      {bucket.replace(/_/g, " ")}:
                    </span>{" "}
                    <span className="text-amber-800">{names.join(", ")}</span>
                  </li>
                ))}
              </ul>
              <p className="text-[11px] text-amber-700 mt-2">
                Para modificar la lista, edita{" "}
                <code className="bg-white/60 px-1 rounded">
                  services/mercadona_exclusions.py
                </code>
                . Los cambios se aplican tanto al pipeline de import como al endpoint de curación.
              </p>
            </div>
          </div>
        </section>
      )}

      {job && (
        <section
          data-testid="import-job-panel"
          className="rounded-xl border border-neutral-200 bg-white p-4 space-y-3"
        >
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold">
              Job <span data-testid="job-id" className="font-mono text-xs">{job.id?.slice(0, 8)}</span>{" "}
              <span
                className={`ml-2 inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                  job.status === "completed"
                    ? "bg-emerald-100 text-emerald-800"
                    : job.status === "failed"
                    ? "bg-red-100 text-red-800"
                    : "bg-blue-100 text-blue-800"
                }`}
                data-testid="job-status"
              >
                {job.status}
              </span>
            </h2>
            {isRunning && <Loader2 className="h-4 w-4 animate-spin text-neutral-500" />}
          </div>
          <div className="text-sm text-neutral-600">
            Fase: <span data-testid="job-phase">{job.progress?.phase || "—"}</span>
            {" · "}
            <span data-testid="job-progress-text">
              {job.progress?.current || 0} / {job.progress?.total || 0}
            </span>
          </div>
          <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
            <div
              data-testid="job-progress-bar"
              className="h-full bg-[#00953B] transition-all"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <Stat label="Categorías creadas" value={job.stats?.categories_created} />
            <Stat label="Productos creados" value={job.stats?.products_created} />
            <Stat label="Productos actualizados" value={job.stats?.products_updated} />
            <Stat label="Imágenes descargadas" value={job.stats?.images_downloaded} />
            <Stat label="Imágenes omitidas" value={job.stats?.images_skipped} />
            <Stat label="Productos borrados" value={job.stats?.products_deleted} />
            <Stat label="Categorías borradas" value={job.stats?.categories_deleted} />
            <Stat label="Errores" value={(job.stats?.errors || []).length} tone="red" />
            {/* Phase 35 — exclusion stats. Only show when non-zero to keep
                the grid clean for legacy jobs that didn't track them. */}
            {(job.stats?.excluded_top_categories || 0) > 0 && (
              <Stat
                label="Top excluidas"
                value={job.stats?.excluded_top_categories}
                tone="amber"
                testid="stat-excluded-top"
              />
            )}
            {(job.stats?.excluded_subcategories || 0) > 0 && (
              <Stat
                label="Subcats excluidas"
                value={job.stats?.excluded_subcategories}
                tone="amber"
                testid="stat-excluded-subs"
              />
            )}
            {(job.stats?.excluded_products || 0) > 0 && (
              <Stat
                label="Productos evitados"
                value={job.stats?.excluded_products}
                tone="amber"
                testid="stat-excluded-products"
              />
            )}
          </div>
          {(job.stats?.excluded_category_names || []).length > 0 && (
            <details className="text-xs" data-testid="job-excluded-details">
              <summary className="cursor-pointer text-amber-800">
                Ver categorías excluidas ({job.stats.excluded_category_names.length})
              </summary>
              <ul className="mt-2 max-h-32 overflow-auto bg-amber-50 border border-amber-100 rounded p-2 text-amber-900 list-disc list-inside">
                {job.stats.excluded_category_names.map((n) => (
                  <li key={n}>{n}</li>
                ))}
              </ul>
            </details>
          )}
          {(job.stats?.errors || []).length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-red-700">
                Ver errores ({job.stats.errors.length})
              </summary>
              <pre className="mt-2 max-h-48 overflow-auto bg-red-50 border border-red-100 rounded p-2 text-red-800">
                {job.stats.errors.slice(-20).join("\n")}
              </pre>
            </details>
          )}
        </section>
      )}

      <section className="rounded-xl border border-neutral-200 bg-white p-4">
        <h2 className="text-sm font-semibold mb-3">Últimas 10 ejecuciones</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-neutral-500">
              <tr>
                <th className="text-left py-1">Inicio</th>
                <th className="text-left py-1">Tipo</th>
                <th className="text-left py-1">Estado</th>
                <th className="text-right py-1">Productos</th>
                <th className="text-right py-1">Imágenes</th>
                <th className="text-right py-1">Errores</th>
              </tr>
            </thead>
            <tbody data-testid="import-history-body">
              {history.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-4 text-neutral-400">
                    Todavía no hay ejecuciones registradas.
                  </td>
                </tr>
              )}
              {history.map((h) => (
                <tr key={h.id} className="border-t border-neutral-100">
                  <td className="py-2 font-mono">{formatDate(h.started_at)}</td>
                  <td>{h.kind}</td>
                  <td>{h.status}</td>
                  <td className="text-right">
                    {(h.stats?.products_created || 0) + (h.stats?.products_updated || 0)}
                  </td>
                  <td className="text-right">{h.stats?.images_downloaded || 0}</td>
                  <td className="text-right">{(h.stats?.errors || []).length}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {confirmOpen && (
        <div
          role="dialog"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          data-testid="import-confirm-modal"
        >
          <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-6 space-y-4">
            <div className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-6 w-6" />
              <h3 className="text-lg font-bold">¿Continuar con la importación?</h3>
            </div>
            <p className="text-sm text-neutral-700">
              Esta acción <b>borrará todos los productos y categorías actuales</b> y los
              reemplazará por el catálogo completo de Mercadona (~4640 productos).
              Los pedidos existentes se conservan pero podrían quedar con referencias
              huérfanas. <b>Esta operación no es reversible.</b>
            </p>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setConfirmOpen(false)}
                data-testid="import-confirm-cancel"
              >
                Cancelar
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={startFullImport}
                data-testid="import-confirm-yes"
              >
                Sí, borrar e importar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone, testid }) {
  const cls =
    tone === "red"
      ? "bg-red-50 border-red-100 text-red-800"
      : tone === "amber"
      ? "bg-amber-50 border-amber-100 text-amber-900"
      : "bg-neutral-50 border-neutral-200 text-neutral-900";
  return (
    <div className={`rounded-lg border p-2 ${cls}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-lg font-bold">{value ?? 0}</div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch (_e) {
    return iso;
  }
}
